<template>
  <div class="google-map-wrap relative-position">
    <div
      v-if="$slots['left-bottom-controls']"
      class="z-index-1 absolute left-bottom-controls"
    >
      <slot name="left-bottom-controls" />
    </div>
    <div
      ref="googleMapRef"
      :class="['google-map', { [`cursor-map-${cursorMap}`]: cursorMap }]"
    />
    <MapControls :map="map" />
    <MapDistanceBlock
      v-if="type !== 'hourly'"
      :distance="distance"
      :time="time"
      :is-round-trip="type === 'round_trip' && !isTransferCreation"
    />
  </div>
</template>

<script setup>
import {
  computed,
  inject,
  onBeforeUnmount,
  onMounted,
  watch,
} from 'vue';

import MapControls from '~components/MapControls';
import MapDistanceBlock from '~components/MapDistanceBlock';

import mapProps from '~common/utils/mapProps';

import useMap from '~composables/useMap';
import useMapMarker from '~composables/useMapMarker';

const moment = inject('moment');
const axios = inject('axios', undefined);

const props = defineProps({
  distance: {
    type: Number,
    default: null,
  },
  time: {
    type: Number,
    default: null,
  },
  from: {
    type: Object,
    default: () => ({}),
  },
  to: {
    type: Object,
    default: () => ({}),
  },
  drivers: {
    type: Array,
    default: () => [],
  },
  asap: {
    type: Boolean,
    default: false,
  },
  draggable: {
    type: Boolean,
    default: false,
  },
  type: {
    type: String,
    default: '',
  },
  noZoomMarker: {
    type: Boolean,
    default: false,
  },
  routeInfo: {
    type: Object,
    default: null,
  },
  cursorMap: {
    type: String,
    default: null,
  },
  zoomPassengerMarker: {
    type: Boolean,
    default: false,
  },
  isTransferCreation: {
    type: Boolean,
    default: false,
  },
  ...mapProps,
});
const emit = defineEmits(['click', 'set-direction', 'click-on-marker', 'map-initialized']);

let abortController;
let mapClickListener;
let styleElement;
let directionsRenderer;

const driversWithLocation = computed(() => props.drivers?.filter(
  (el) => el.location && Object.keys(el.location).length,
));

const {
  map,
  markers,
  disableZoom,
  clearMarker,
  clearMarkers,
  driverStyles,
  zoomMarker,
  googleMapRef,
  geocode,
  pointToLatLng,
  setMapCenter,
  addDisableZoomListeners,
  newLocation,
} = useMap(props, init);
const { createMapMarker } = useMapMarker();

function initRoute() {
  directionsRenderer = new google.maps.DirectionsRenderer({
    suppressMarkers: true,
    suppressInfoWindows: true,
    preserveViewport: true,
    polylineOptions: {
      strokeColor: '#31b700',
      strokeWeight: 5,
    },
  });
}

function handleDrivers(drivers, oldDrivers = []) {
  drivers.forEach((driver) => {
    newLocation(driver, driver.location);
  });
  styleElement.innerHTML = driverStyles.join('\n');
  oldDrivers.forEach(({ id }) => {
    if (!drivers.some((driver) => driver.id === id)) {
      clearMarker(id);
    }
  });
}

async function onMarkerDragEnd(direction, { latLng }) {
  disableZoom.value = true;

  emit('set-direction', direction, await geocode({ latLng }), true);
  clearMarker(direction);
}

function setMapsMarker(key, point) {
  const position = pointToLatLng(point);
  const marker = markers[key];
  if (marker) {
    marker.position = position;
    return;
  }

  markers[key] = createMapMarker(google.maps.OverlayView, {
    position,
    map: map.value,
    animated: props.directionToZoom === key,
    direction: key,
    draggable: props.draggable,
    onDragEnd: onMarkerDragEnd,
  });
  markers[key].marker.addListener('click', () => emit('click-on-marker', key));
}

function decodePath(points) {
  return google.maps.geometry.encoding.decodePath(points);
}

function decodeRoutePaths(route) {
  route.overview_path = decodePath(route.overview_polyline.points);
  route.legs[0].steps.forEach((step) => {
    step.path = decodePath(step.polyline.points);
  });
}

function clearRoute() {
  if (directionsRenderer) {
    directionsRenderer.setMap(null);
  }
}

async function setRoute() {
  if (props.type === 'hourly' || !markers?.from || !markers?.to) return;

  clearRoute();

  abortController = new AbortController();

  try {
    const data = props.routeInfo || (await axios('/api/route_info', {
      params: {
        points: [props.from.point, props.to.point],
        date_to: props.asap
          ? moment().format('YYYY-MM-DDTHH:mm:ssZ')
          : undefined,
      },
      signal: abortController.signal,
    })).data;

    if (!data.routes) return;

    decodeRoutePaths(data.routes[0]);
    directionsRenderer.setDirections(data);
    directionsRenderer.setMap(map.value);
  } catch (err) {
    if (err.name !== 'CanceledError') {
      console.error(err);
    }
  }
}

function setFromToMarker(key, { point }) {
  if (point) {
    setMapsMarker(key, point);
    if (!props.noZoomMarker) {
      zoomMarker();
    }
  } else {
    clearMarker(key);
  }

  if (props.from.point && props.to?.point) {
    setRoute();
  } else {
    clearRoute();
  }
}

function setRoutePoints() {
  if (props.from) {
    setFromToMarker('from', props.from);
  }

  if (props.to) {
    setFromToMarker('to', props.to);
  }
}

function init() {
  initRoute();
  setRoutePoints();
  mapClickListener = map.value.addListener('click', (e) => {
    emit('click', e);
  });
  watch(() => props.from, (value) => setFromToMarker('from', value));
  watch(() => props.to, (value) => setFromToMarker('to', value));
  watch(() => props.routeInfo, () => setRoute());
  watch(driversWithLocation, handleDrivers, { immediate: true });
}

onMounted(() => {
  styleElement = document.createElement('style');
  document.head.appendChild(styleElement);
});

onBeforeUnmount(() => {
  abortController?.abort();
  clearRoute();
  clearMarkers();
  mapClickListener?.remove();
  styleElement.remove();
});

defineExpose({
  map,
  pointToLatLng,
  setMapCenter,
  addDisableZoomListeners,
  newLocation,
  clearMarker,
});
</script>

<style scoped lang="scss">
.google-map-wrap {
  height: 400px;

  .left-bottom-controls {
    left: 16px;
    bottom: 32px;
  }

  .google-map {
    height: 100%;
  }

  @media (min-width: $breakpoint-md-min) {
    height: 477px;
  }

  @media (min-width: $breakpoint-lg-min) {
    height: 577px;
  }
}

.fix-gm :deep() {
  @media (max-width: $breakpoint-sm-max) {
    .gm-style a[href^="https://maps.google.com/maps"],
    .gm-style-cc {
      display: block !important;
      margin-bottom: 16px;
    }
  }
}

.map-distance-block {
  position: absolute;
  left: 50%;
  transform: translate(-50%);
  bottom: 16px;

  @media (max-width: $breakpoint-sm-max) {
    bottom: 24px;
  }
}
</style>

<style lang="scss">
@each $point in 'from', 'to' {
  .google-map.cursor-map-#{$point} * {
    cursor: url('/svg/gettransfer/map_cursor_#{$point}.svg') 18 46, auto;
  }
}
</style>
